import { useEffect, useRef, useState } from "react";
import "./dashboard.scss";
import { BrandButtonWhite } from "../components/form/buttons";
import {
  logoutUser,
  userLoggedIn,
  getUserInfo,
  setAppInfo,
  updateCurrentExamData,
  getCurrentSectionandQuestion,
  sanitizeServerData,
} from "../components/user";
import { useNavigate } from "react-router-dom";
import { UserInfoRecord } from "../components/types";
import { useQuery } from "react-query";
import axios from "axios";
import { ApiEndpoint } from "../components/utils";
import LoadError from "../components/error";
import ExamList from "../components/examlist";
import Loader from "../components/loader";
import DashboardTabs from "../components/dashboardTabs";
import { ExamRecord } from "../components/types";
import { Snackbar, Alert } from "@mui/material";
import ChooseExamType from "../components/chooseExamType";

const Dashboard = () => {
  const navigate = useNavigate();
  const [currTab, setCurrentTab] = useState<number>(0);
  const [uData, setUserData] = useState<UserInfoRecord | null>(null);
  const dashboardData = useRef<ExamRecord[]>([]);
  const [examList, setExamList] = useState<ExamRecord[] | null>(null);
  const [load, setLoad] = useState<boolean>(false);
  const [snackError, setSnackError] = useState(false);
  const vertical = "bottom";
  const horizontal = "center";
  const [examTypeOpen, setExamTypeOpen] = useState(false);

  const { error, isLoading } = useQuery(
    "dashboardData",
    async () => {
      const resp = await axios.post(
        ApiEndpoint + "wp-json/satapp-api/v1/userDashboard",
        {
          token: uData?.token,
        }
      );

      // get dashboard data
      dashboardData.current = resp.data.exams as ExamRecord[];
      setAppInfo({ examData: dashboardData.current });
      filterExams();

      // get config data
      const resp2 = await axios.get(
        ApiEndpoint + `wp-json/satapp-api/v1/configData?token=${uData?.token}`
      );
      setAppInfo({ configData: resp2.data });

      setLoad(false);
    },
    {
      enabled: !!load,
    }
  );

  const logout = () => {
    logoutUser();
    navigate("/");
  };

  const closeSnack = () => {
    setSnackError(false);
  };

  const getExamData = async (
    examId: number,
    init = false,
    getResults = false,
    snapshotID?: number
  ) => {
    setLoad(true);
    setSnackError(false);

    const resp = await axios.post(
      ApiEndpoint + "wp-json/satapp-api/v1/getExamData",
      {
        token: uData?.token,
        exam_id: examId,
        snapshotID: snapshotID,
      }
    );

    if (resp.data.success) {
      resp.data.examId = examId;
      resp.data = sanitizeServerData(resp.data);
      updateCurrentExamData(examId, resp.data, true);

      if (getResults) {
        navigate("/exam-results", { replace: true, state: { snapshotID } });
        return;
      }

      if (init) {
        setExamTypeOpen(true);
      } else {
        let targetQuestionId = null;
        const { currentSectionId, currentQuestionId } =
          getCurrentSectionandQuestion();
        if (!currentQuestionId) {
          targetQuestionId =
            resp.data.examAnswers[currentSectionId as number].questions[0]
              .questionId;
          updateCurrentExamData(
            examId,
            { lastQuestionAnswered: targetQuestionId },
            false
          );
        } else {
          targetQuestionId = currentQuestionId;
        }
        if (currentSectionId || currentSectionId == 0) {
          navigate(`/question/${targetQuestionId}`, { replace: true });
        } else {
          setSnackError(true);
        }
      }
    } else {
      setSnackError(true);
    }

    setLoad(false);
  };

  const startExam = (examId: number) => {
    if (uData?.userType === 0) {
      getExamData(examId, true);
    } else {
      const examData = dashboardData.current.filter(
        (exam: ExamRecord) => exam.examId === examId
      )[0] as ExamRecord;
      if (examData.paid) {
        getExamData(examId, true);
      } else {
        navigate("/register-coupon", {
          state: { examId: examData.examId, examFee: examData.examFee },
        });
      }
    }
  };

  const setExamType = (type: number | string) => {
    navigate(`/exam-landing`, {
      replace: true,
      state: { type },
    });
  };

  const continueExam = (examId: number) => {
    getExamData(examId);
  };

  const viewSnapshotResults = (examId: number, snapshotID: number) => {
    getExamData(examId, false, true, snapshotID);
  };

  const restartExam = async (examId: number) => {
    await axios.post(`${ApiEndpoint}wp-json/satapp-api/v1/updateUserExam`, {
      token: uData?.token,
      exam_id: examId,
      userData: {
        currentSectionId: null,
        lastQuestionAnswered: null,
        minutesRemaining: null,
        satVerbalLevel: 0,
        satMathLevel: 0,
        examType: 0,
        sectionStartTime: null,
        examStarted: false,
        examComplete: false,
      },
      userID: uData?.userid,
      resetAsnwers: true,
    });
    startExam(examId);
  };

  const filterExams = () => {
    if (dashboardData.current === null) return [];

    setExamList(
      dashboardData.current.filter((exam: ExamRecord) => {
        if (currTab === 0) {
          return true;
        } else if (currTab === 1 && exam.examStatus === "inProgress") {
          return true;
        } else if (currTab === 2 && exam.examStatus === "completed") {
          return true;
        } else if (currTab === 3 && exam.examStatus === "new") {
          return true;
        }
        return false;
      })
    );
  };

  useEffect(() => {
    if (uData) {
      setLoad(true);
    }
  }, [uData]);

  useEffect(() => {
    filterExams();
  }, [currTab]);

  useEffect(() => {
    if (!userLoggedIn()) {
      navigate("/");
    } else {
      const userinfo = getUserInfo() as UserInfoRecord;
      if (userinfo.userType === 0 && !userinfo.paid) {
        navigate("/register-coupon", {
          replace: true,
          state: {
            examId: -1,
            examFee: userinfo?.fullExamFee,
            cCode: userinfo?.couponCode,
          },
        });
      } else {
        setUserData(userinfo);
      }
    }
  }, []);

  if (error) return <LoadError />;
  if (isLoading || load) return <Loader />;

  return (
    <div className="dashboardPage">
      <div className="pageHeader">
        <div className="userInfo">
          <h1>Hello, {uData?.first_name}</h1>
          <p>
            Here’s a list of your tests, and some additional instructions etc.
          </p>
        </div>
        <div>
          {uData?.isadmin ||
            (uData?.istutor && (
              <BrandButtonWhite
                onClick={() => navigate("/admin")}
                variant="outlined"
                style={{ marginRight: "20px" }}
              >
                ADMIN
              </BrandButtonWhite>
            ))}
          <BrandButtonWhite onClick={logout} variant="outlined">
            LOGOUT
          </BrandButtonWhite>
        </div>
      </div>

      <DashboardTabs activeTab={currTab} setCurrentTab={setCurrentTab} />
      <div className="resultsList">
        <ExamList
          exams={examList}
          startExam={startExam}
          continueExam={continueExam}
          viewSnapshotResults={viewSnapshotResults}
          restartExam={restartExam}
        />
      </div>
      <Snackbar
        open={snackError}
        autoHideDuration={6000}
        onClose={closeSnack}
        anchorOrigin={{ vertical, horizontal }}
      >
        <Alert severity="error">
          There was an error starting the exam. Please refresh or contact the
          administrator.
        </Alert>
      </Snackbar>
      <ChooseExamType open={examTypeOpen} setExamType={setExamType} />
    </div>
  );
};

export default Dashboard;
